import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Lock, Menu, Plus, ShieldCheck, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/Avatar';
import { Button } from '../ui/Button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/DropdownMenu';
import { ConnectionStatus } from '../ConnectionStatus';
import { ExportMenu } from '../export/ExportMenu';
import { ThemeToggle } from '../ThemeToggle';
import { useAuth } from '../../contexts/AuthContext';
import { useStudents } from '../../contexts/StudentsContext';
import { navItems } from '../../data/navigation';
import { initialsOf } from '../../utils/format';
import { accountProfile } from '../../utils/profile';
interface TopbarProps {
  onOpenMobileNav: () => void;
  sidebarCollapsed: boolean;
}
export function Topbar({
  onOpenMobileNav,
  sidebarCollapsed
}: TopbarProps) {
  const {
    user,
    logout,
    lock
  } = useAuth();
  const {
    openCreateForm
  } = useStudents();
  const location = useLocation();
  const navigate = useNavigate();
  const active = navItems.find(item => location.pathname.startsWith(item.to)) ?? navItems[0];
  const profile = accountProfile(user);
  const displayName = profile.name;
  const handleSignOut = async () => {
    try {
      await logout();
      toast.success('Signed out safely.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not sign out.');
    }
    navigate('/', {
      replace: true
    });
  };
  const handleLock = () => {
    lock();
    toast.info('App locked. Enter your master password to continue.');
    navigate('/security', {
      replace: true
    });
  };
  return <header className={`glass sticky top-0 z-20 flex h-16 items-center gap-3 rounded-b-3xl border-t-0 px-4 transition-[margin] duration-300 sm:px-6 ${sidebarCollapsed ? 'lg:ml-[84px]' : 'lg:ml-[264px]'}`} data-id="element-211" data-mp="3hq2zcgef686g">
      <button type="button" onClick={onOpenMobileNav} aria-label="Open navigation" className="grid h-9 w-9 place-items-center rounded-xl border border-[var(--glass-hairline)] text-muted-foreground transition-colors hover:text-foreground lg:hidden" data-id="element-212" data-mp="1gqg546fji3mq">
        <Menu className="h-4 w-4" data-id="element-213" data-mp="1vngysfhak8nu" />
      </button>

      <div className="min-w-0 flex-1" data-id="element-214" data-mp="1znr19i5vkgxy">
        <h1 className="truncate text-base font-semibold tracking-tight text-foreground" data-id="element-215" data-mp="1zmps5j8f64iv">{active.label}</h1>
        <p className="hidden truncate text-xs text-muted-foreground sm:block" data-id="element-216" data-mp="24kzovy9sld6l">{active.description}</p>
      </div>

      <div className="flex items-center gap-2" data-id="element-217" data-mp="29e2g3zr3kajm">
        <ConnectionStatus data-id="element-218" data-mp="2ecx2tcs6qj90" />

        <ExportMenu data-id="element-219" data-mp="2eb2h3w93an3v" />

        <Button onClick={openCreateForm} className="hidden h-9 rounded-xl brand-gradient px-4 text-white shadow-lg shadow-indigo-500/25 hover:opacity-95 sm:inline-flex" data-id="element-220" data-mp="2eegfyuijoe6k">
          <Plus className="mr-1.5 h-4 w-4" data-id="element-221" data-mp="2naz9o8ylo647" />
          Add Student
        </Button>

        <ThemeToggle data-id="element-222" data-mp="br9phubol0ek" />

        <button type="button" onClick={handleSignOut} aria-label="Log out" title="Log out" className="glass-flat hidden h-9 items-center gap-1.5 rounded-xl px-3 text-xs font-medium text-muted-foreground transition-colors hover:text-destructive sm:inline-flex" data-id="element-223" data-mp="bndltb1hzmsc">
          <LogOut className="h-3.5 w-3.5" data-id="element-224" data-mp="vdfvjvf6zxem" />
          Log out
        </button>

        <DropdownMenu data-id="element-225" data-mp="vfij88svheg6">
          <DropdownMenuTrigger asChild data-id="element-226" data-mp="qe2ykhulx9yb">
            <button type="button" aria-label="Account menu" className="rounded-full ring-offset-2 ring-offset-background transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-227" data-mp="qf4nnq8gjwz1">
              <Avatar className="h-9 w-9 border border-[var(--glass-border)]" data-id="element-228" data-mp="1e4greal0ncty">
                {profile.avatarUrl ? <AvatarImage src={profile.avatarUrl} alt={displayName} data-id="element-229" data-mp="19d72xdb7efjn" /> : null}
                <AvatarFallback className="brand-gradient text-xs font-semibold text-white" data-id="element-230" data-mp="tvpfbedk5y8n">
                  {initialsOf(displayName)}
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64" data-id="element-231" data-mp="jyue2npwhjqo">
            <DropdownMenuLabel data-id="element-232" data-mp="ovumh5a82yky">
              <span className="block truncate text-sm font-semibold text-foreground" data-id="element-233" data-mp="ox5s7an8agep">{displayName}</span>
              <span className="block truncate text-xs font-normal text-muted-foreground" data-id="element-234" data-mp="1cmgskr0o28sb">{profile.email}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator data-id="element-235" data-mp="12w5ggh6twtac" />
            <DropdownMenuItem onClick={() => navigate('/settings')} data-id="element-236" data-mp="12v43u8a5zyqi">
              <UserRound className="mr-2 h-4 w-4" data-id="element-237" data-mp="17uytk8i7ei9z" />
              Account settings
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/settings')} data-id="element-238" data-mp="1vkjc4qag3diq">
              <ShieldCheck className="mr-2 h-4 w-4" data-id="element-239" data-mp="20bj7vs7tvpjn" />
              Master password
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLock} data-id="element-240" data-mp="3l1op0fvg4cjb">
              <Lock className="mr-2 h-4 w-4" data-id="element-241" data-mp="3l03tamxeanjv" />
              Lock app
            </DropdownMenuItem>
            <DropdownMenuSeparator data-id="element-242" data-mp="3g2li0pvwnw8p" />
            <DropdownMenuItem variant="destructive" onClick={handleSignOut} data-id="element-243" data-mp="3tu0d3v337nw4">
              <LogOut className="mr-2 h-4 w-4" data-id="element-244" data-mp="3twcv288uhy96" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>;
}